import React, { useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { CERT_TYPES, AGR_TYPES, fmt } from "@/lib/legaldocs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Search, FileText, Trash2, Printer, ScrollText } from "lucide-react";
import { toast } from "sonner";

export default function LegalDocs({ docClass }) {
  const TYPES = docClass === "certificate" ? CERT_TYPES : AGR_TYPES;
  const heading = docClass === "certificate" ? "Certificates" : "Agreements";
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState({});
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState(Object.keys(TYPES)[0]);
  const [form, setForm] = useState({});
  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await http.get("/legal-docs", { params: { doc_class: docClass } });
      setRows(data);
    } catch (error) {
      setLoadError(requestErrorMessage(error, `load ${heading.toLowerCase()}`));
    } finally {
      setLoading(false);
    }
  };
  const loadReferences = async () => {
    setReferenceError("");
    const [companyResult, customersResult] = await Promise.allSettled([http.get("/company"), http.get("/customers")]);
    const errors = [];
    if (companyResult.status === "fulfilled") setCompany(companyResult.value.data);
    else errors.push(requestErrorMessage(companyResult.reason, "load company details"));
    if (customersResult.status === "fulfilled" && Array.isArray(customersResult.value.data)) setCustomers(customersResult.value.data);
    else if (customersResult.status === "fulfilled") errors.push("Could not load customers because the response was not in the expected format.");
    else errors.push(requestErrorMessage(customersResult.reason, "load customers"));
    if (errors.length) setReferenceError(errors.join(" "));
  };
  useEffect(() => { load(); loadReferences(); }, [docClass]);

  const openNew = () => { setDocType(Object.keys(TYPES)[0]); setForm({ place: company.city || "", doc_date: new Date().toLocaleDateString("en-CA") }); setOpen(true); };
  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickCustomer = (id) => { const c = customers.find((x) => x.id === id); if (c) setForm((f) => ({ ...f, party_name: c.name, party_address: c.billing_address || "", party_gstin: c.gstin || "", party_phone: c.phone || "" })); };

  const save = async () => {
    if (!(form.party_name || "").trim()) return toast.error("Enter or pick a party");
    try {
      const { data } = await http.post("/legal-docs", { ...form, doc_class: docClass, doc_type: docType });
      toast.success(`${TYPES[docType].label} created`);
      setOpen(false); load(); openView(data.id);
    } catch (e) { toast.error(requestErrorMessage(e, "create this document")); }
  };
  const openView = async (id) => {
    try {
      const { data } = await http.get(`/legal-docs/${id}`);
      setView(data);
    } catch (error) { toast.error(requestErrorMessage(error, "load this document")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete document?")) return;
    try {
      await http.delete(`/legal-docs/${id}`);
      toast.success("Deleted");
      load();
    } catch (error) { toast.error(requestErrorMessage(error, "delete this document")); }
  };

  const filtered = rows.filter((r) => `${r.doc_no} ${r.party_name} ${r.doc_type}`.toLowerCase().includes(search.toLowerCase()));
  const def = TYPES[docType];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{heading}</h1>
          <p className="text-sm text-muted-foreground">Create, print & manage {docClass === "certificate" ? "authorization certificates" : "agreements"}</p></div>
        <Button onClick={openNew} data-testid={`add-${docClass}-button`}><Plus className="h-4 w-4 mr-1" /> New {docClass === "certificate" ? "Certificate" : "Agreement"}</Button>
      </div>
      {referenceError && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{referenceError} <Button variant="outline" size="sm" className="ml-2" onClick={loadReferences}>Retry</Button></div>}
      {loadError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError} <Button variant="outline" size="sm" className="ml-2" onClick={load}>Retry</Button></div>}

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><div className="relative max-w-sm"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid={`${docClass}-search`} /></div></div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">No</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">Party</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading documents…</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`${docClass}-row-${r.id}`}>
                <td className="px-4 py-3 font-mono font-medium">{r.doc_no}</td>
                <td className="px-4 py-3">{TYPES[r.doc_type]?.label || r.doc_type}</td>
                <td className="px-4 py-3">{r.party_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(r.doc_date)}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <button onClick={() => openView(r.id)} className="p-1.5 hover:bg-slate-100 rounded" title="View / Print" data-testid={`view-${docClass}-${r.id}`}><Printer className="h-4 w-4 text-slate-600" /></button>
                  <button onClick={() => del(r.id)} className="p-1.5 hover:bg-rose-50 rounded" title="Delete"><Trash2 className="h-4 w-4 text-rose-500" /></button>
                </div></td>
              </tr>
            ))}
            {!loading && !loadError && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground"><ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />None yet</td></tr>}
          </tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New {docClass === "certificate" ? "Certificate" : "Agreement"}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Type</Label>
              <Select value={docType} onValueChange={(v) => setDocType(v)}>
                <SelectTrigger className="mt-1.5" data-testid={`${docClass}-type-select`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pick Customer (optional)</Label>
              <Select onValueChange={pickCustomer}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Fill party from a customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Party Name *</Label><Input value={form.party_name || ""} onChange={(e) => patch("party_name", e.target.value)} className="mt-1.5" data-testid={`${docClass}-party-input`} /></div>
              <div><Label>Phone</Label><Input value={form.party_phone || ""} onChange={(e) => patch("party_phone", e.target.value)} className="mt-1.5" /></div>
              <div className="sm:col-span-2"><Label>Address</Label><Input value={form.party_address || ""} onChange={(e) => patch("party_address", e.target.value)} className="mt-1.5" /></div>
              <div><Label>GSTIN</Label><Input value={form.party_gstin || ""} onChange={(e) => patch("party_gstin", e.target.value)} className="mt-1.5 font-mono" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {def.fields.map((f) => (
                <div key={f.key}><Label>{f.label}</Label><Input type={f.type === "date" ? "date" : "text"} value={form[f.key] || ""} onChange={(e) => patch(f.key, e.target.value)} className="mt-1.5" data-testid={`field-${f.key}`} /></div>
              ))}
              <div><Label>Place</Label><Input value={form.place || ""} onChange={(e) => patch("place", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Date</Label><Input type="date" value={form.doc_date || ""} onChange={(e) => patch("doc_date", e.target.value)} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid={`save-${docClass}-button`}>Create & Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{view && (TYPES[view.doc_type]?.label || view.doc_type)}</DialogTitle></DialogHeader>
          {view && (() => {
            const c = view.company || company;
            const d = TYPES[view.doc_type];
            return (<>
              <div id="print-area" className="bg-white text-slate-900 p-8 border rounded-lg" style={{ minHeight: "260mm" }}>
                <div className="text-center border-b-2 border-slate-800 pb-4">
                  <div className="font-heading text-2xl font-bold">{c.legal_name || c.name}</div>
                  <div className="text-xs text-slate-600">{c.address} {c.city} · GSTIN {c.gstin}</div>
                </div>
                <div className="text-center mt-6"><span className="inline-block border-2 border-brand-600 text-brand-700 px-6 py-1.5 rounded font-heading font-bold uppercase tracking-wider">{d.label}</span></div>
                <div className="flex justify-between text-xs text-slate-500 mt-4"><span>Ref: {view.doc_no}</span><span>Date: {fmt(view.doc_date)}</span></div>
                <p className="mt-8 text-[15px] leading-8 text-justify">{d.body(view, c)}</p>
                {view.party_gstin && <p className="mt-3 text-sm text-slate-600">Party GSTIN: <span className="font-mono">{view.party_gstin}</span></p>}
                <div className="flex justify-between items-end mt-24">
                  <div className="text-sm"><div>Place: {view.place || c.city || "—"}</div><div>Date: {fmt(view.doc_date)}</div></div>
                  <div className="text-center"><div className="border-t border-slate-800 pt-1 px-8 font-semibold">Authorized Signatory</div><div className="text-xs text-slate-500 mt-1">For {c.legal_name || c.name}</div></div>
                </div>
              </div>
              <DialogFooter className="no-print"><Button variant="outline" onClick={() => setView(null)}>Close</Button><Button variant="print" onClick={() => window.print()} data-testid="print-legaldoc-button"><Printer className="h-4 w-4 mr-1" /> Print</Button></DialogFooter>
            </>);
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
